import type { ForeplayAd } from '@/types'

/**
 * Deduplicate ads by URL (or fallback to download_s3_url or id)
 */
export function deduplicateAds(ads: ForeplayAd[]): ForeplayAd[] {
  const seen = new Set<string>()
  return ads.filter(ad => {
    const key = ad.url || ad.download_s3_url || ad.id
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * Deduplicate between two lists - removes items from secondary that exist in primary
 * Used to ensure an ad doesn't appear in both "Product Related" and "Competitor Brands"
 */
export function deduplicateBetweenLists(
  primary: ForeplayAd[],
  secondary: ForeplayAd[]
): ForeplayAd[] {
  const primaryKeys = new Set(
    primary.map(ad => ad.url || ad.download_s3_url || ad.id)
  )
  return secondary.filter(ad => {
    const key = ad.url || ad.download_s3_url || ad.id
    return !primaryKeys.has(key)
  })
}

/**
 * Apply deduplication to both product and competitor ads
 * 1. Deduplicate within each list
 * 2. Remove competitor ads that appear in product ads
 */
export function deduplicateAllAds(
  productAds: ForeplayAd[],
  competitorAds: ForeplayAd[]
): { productAds: ForeplayAd[]; competitorAds: ForeplayAd[] } {
  // First deduplicate within each list
  const uniqueProductAds = deduplicateAds(productAds)
  const uniqueCompetitorAds = deduplicateAds(competitorAds)

  // Then remove competitor ads that appear in product ads
  const filteredCompetitorAds = deduplicateBetweenLists(
    uniqueProductAds,
    uniqueCompetitorAds
  )

  return {
    productAds: uniqueProductAds,
    competitorAds: filteredCompetitorAds,
  }
}

/**
 * Get ad score from either the ad object or a scores map
 */
export function getAdScore(
  ad: ForeplayAd,
  scoredAdsMap?: Map<string, number>
): number | undefined {
  // First check the scores map
  if (scoredAdsMap?.has(ad.id)) {
    return scoredAdsMap.get(ad.id)
  }
  // Fallback to ad's overall_score property
  return ad.overall_score ?? undefined
}

/**
 * Sort ads by score (highest first) and select top N
 */
export function selectTopNByScore(
  ads: ForeplayAd[],
  n: number,
  scoredAdsMap?: Map<string, number>
): string[] {
  // Create array of ads with their scores
  const adsWithScores = ads.map(ad => ({
    id: ad.id,
    score: getAdScore(ad, scoredAdsMap) ?? 0,
  }))

  // Sort by score descending
  adsWithScores.sort((a, b) => b.score - a.score)

  // Return top N ids
  return adsWithScores.slice(0, n).map(item => item.id)
}

/**
 * Merge all ads from product and competitor lists, deduplicated
 */
export function mergeAllAds(
  productAds: ForeplayAd[],
  competitorAds: ForeplayAd[]
): ForeplayAd[] {
  const { productAds: uniqueProduct, competitorAds: uniqueCompetitor } =
    deduplicateAllAds(productAds, competitorAds)
  return [...uniqueProduct, ...uniqueCompetitor]
}

export type SelectionMode = 'auto' | 'manual'

export const MIN_SELECTION = 1
export const MAX_SELECTION = 10
export const DEFAULT_AUTO_COUNT = 3
